import React, { useEffect, useState } from "react";
import {
  Exercise,
  ExerciseContainer,
} from "../../libs/exercises/types";
import {
  useDeleteExerciseMutation,
  useMoveExerciseToContainerMutation,
  useUpdateExerciseContainerMutation,
} from "../../libs/exercises/api";
import {
  ColorPicker,
  DEFAULT_CONTAINER_COLOR,
} from "../ProgressionsTable/ColorPicker";
import { ExerciseContainerCard } from "./ExerciseContainerCard";
import { ExerciseCard, EXERCISE_DRAG_TYPE } from "./ExerciseCard";
import { CreateExerciseContainerModal } from "./CreateExerciseContainerModal";
import { DeleteExerciseContainerModal } from "./DeleteExerciseContainerModal";
import { CreateExerciseModal } from "./CreateExerciseModal";
import { EditExerciseModal } from "./EditExerciseModal";
import { DeleteExerciseModal } from "./DeleteExerciseModal";

const MAX_DEPTH = 4;

type Props = {
  exercises: Exercise[];
  containers: ExerciseContainer[];
};

export const ExerciseFolders: React.FC<Props> = ({ exercises, containers }) => {
  const [path, setPath] = useState<number[]>([]);
  const [search, setSearch] = useState("");

  const [openModal, setOpenModal] = useState<string | undefined>();
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  const [containerToDelete, setContainerToDelete] = useState<ExerciseContainer>();
  const [containerToRename, setContainerToRename] = useState<ExerciseContainer>();
  const [renameValue, setRenameValue] = useState("");
  const [renameColor, setRenameColor] = useState(DEFAULT_CONTAINER_COLOR);
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState(false);

  // Modali esercizio
  const [exerciseToEdit, setExerciseToEdit] = useState<Exercise | undefined>();
  const [exerciseToDelete, setExerciseToDelete] = useState<Exercise | undefined>();

  const [moveExercise] = useMoveExerciseToContainerMutation();
  const [updateContainer] = useUpdateExerciseContainerMutation();
  const [deleteExercise] = useDeleteExerciseMutation();

  // Selezione multipla
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      for (const id of selectedIds) {
        await deleteExercise(id).unwrap();
      }
    } catch (err) {
      console.error("Errore durante la cancellazione multipla:", err);
    } finally {
      setBulkDeleting(false);
      setConfirmBulkDelete(false);
      exitSelectionMode();
    }
  };

  // Validazione path
  useEffect(() => {
    const validUpTo = path.findIndex((id) => !containers.some((c) => c.id === id));
    if (validUpTo !== -1) setPath((p) => p.slice(0, validUpTo));
  }, [containers, path]);

  // Reset search on navigation
  useEffect(() => setSearch(""), [path]);

  const childrenOf = (parentId: number) =>
    containers.filter((c) => c.parentId === parentId);
  const exercisesIn = (containerId: number) =>
    exercises.filter((e) => e.idContainer === containerId);
  const orphanExercises = exercises.filter(
    (e) => e.idContainer == null || !containers.some((c) => c.id === e.idContainer)
  );

  const currentContainerId = path.length ? path[path.length - 1] : null;
  const currentContainer = containers.find((c) => c.id === currentContainerId);
  const currentDepth = path.length;

  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const matchesText = (s?: string | null) => (s ?? "").toLowerCase().includes(q);
  const matchesFolder = (c: ExerciseContainer) => matchesText(c.name);
  const matchesExercise = (e: Exercise) =>
    matchesText(e.name) || matchesText(e.notes) || (e.tags || []).some((t) => matchesText(t));

  const containerColorOf = (e: Exercise) =>
    containers.find((c) => c.id === e.idContainer)?.color;

  const pathTo = (c: ExerciseContainer): number[] => {
    const ids: number[] = [];
    let cur: ExerciseContainer | undefined = c;
    const guard = new Set<number>();
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      ids.unshift(cur.id);
      cur =
        cur.parentId != null
          ? containers.find((x) => x.id === cur!.parentId)
          : undefined;
    }
    return ids;
  };

  const openFolder = (c: ExerciseContainer) => setPath(pathTo(c));

  const foldersToShow = searching
    ? containers.filter(matchesFolder)
    : currentContainerId == null
    ? containers.filter((c) => c.parentId == null)
    : childrenOf(currentContainerId);

  const exercisesToShow = searching
    ? exercises.filter(matchesExercise)
    : currentContainerId == null
    ? orphanExercises
    : exercisesIn(currentContainerId);

  const handleDropExercise = (exerciseId: string, targetContainerId: number | null) => {
    moveExercise({ id: exerciseId, idContainer: targetContainerId })
      .unwrap()
      .catch((err) => {
        console.error("Spostamento esercizio fallito:", err);
        const status = err?.status ?? "?";
        const body =
          typeof err?.data === "string"
            ? err.data
            : JSON.stringify(err?.data ?? err);
        alert(`Spostamento non riuscito (status ${status}): ${body}`);
      });
  };

  const openRename = (container: ExerciseContainer) => {
    setContainerToRename(container);
    setRenameValue(container.name);
    setRenameColor(container.color || DEFAULT_CONTAINER_COLOR);
    setRenameError(false);
    setOpenModal("renameExerciseContainer");
  };

  const handleRenameSubmit = () => {
    if (!containerToRename || !renameValue.trim()) return;
    setRenameSaving(true);
    setRenameError(false);
    updateContainer({
      id: containerToRename.id,
      name: renameValue.trim(),
      color: renameColor,
      parentId: containerToRename.parentId ?? null,
    })
      .unwrap()
      .then(() => {
        setContainerToRename(undefined);
        setOpenModal(undefined);
        setRenameSaving(false);
      })
      .catch(() => {
        setRenameSaving(false);
        setRenameError(true);
      });
  };

  const depthLimitReached = currentDepth >= MAX_DEPTH;
  const canAddSubfolder = currentContainerId != null && !depthLimitReached;

  const breadcrumbDropProps = (targetId: number | null) => ({
    onDragOver: (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes(EXERCISE_DRAG_TYPE)) e.preventDefault();
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const data = e.dataTransfer.getData(EXERCISE_DRAG_TYPE);
      if (!data) return;
      try {
        const dragged = JSON.parse(data) as {
          id: string;
          idContainer: number | null;
        };
        if (dragged.idContainer !== targetId) handleDropExercise(dragged.id, targetId);
      } catch {}
    },
  });

  return (
    <div className="p-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4 text-sm flex-wrap">
        <button
          onClick={() => setPath([])}
          {...breadcrumbDropProps(null)}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Esercizi
        </button>
        {path.map((id, i) => {
          const c = containers.find((x) => x.id === id);
          const isLast = i === path.length - 1;
          return (
            <React.Fragment key={id}>
              <span className="text-gray-400">/</span>
              {isLast ? (
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                  {c?.name ?? "?"}
                </span>
              ) : (
                <button
                  onClick={() => setPath(path.slice(0, i + 1))}
                  {...breadcrumbDropProps(id)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {c?.name ?? "?"}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Actions bar */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
        <div className="relative w-full md:w-1/2">
          <input
            type="text"
            placeholder="Cerca in tutte le cartelle (nome, tag o note)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full py-2 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-3">
          {selectionMode ? (
            <>
              <button
                onClick={() => setConfirmBulkDelete(true)}
                disabled={selectedIds.size === 0}
                className="text-white bg-red-600 hover:bg-red-700 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Elimina ({selectedIds.size})
              </button>
              <button
                onClick={exitSelectionMode}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium text-sm px-4 py-2.5"
              >
                Annulla
              </button>
            </>
          ) : (
            <>
              {exercisesToShow.length > 0 && (
                <button
                  onClick={() => setSelectionMode(true)}
                  className="text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
                >
                  Seleziona
                </button>
              )}
              <button
                onClick={() => setOpenModal("create-exercise")}
                className="text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
              >
                Nuovo esercizio
              </button>
              {currentContainerId == null ? (
                <button
                  onClick={() => {
                    setCreateParentId(null);
                    setOpenModal("createExerciseContainer");
                  }}
                  className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
                >
                  Nuova cartella
                </button>
              ) : (
                <button
                  disabled={!canAddSubfolder}
                  title={
                    canAddSubfolder
                      ? undefined
                      : `Profondita' massima: ${MAX_DEPTH} livelli`
                  }
                  onClick={() => {
                    setCreateParentId(currentContainerId);
                    setOpenModal("createExerciseContainer");
                  }}
                  className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Nuova sotto-cartella
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Grid: cartelle poi esercizi */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {foldersToShow.map((container) => {
          const subCount = childrenOf(container.id).length;
          const exs = exercisesIn(container.id);
          return (
            <ExerciseContainerCard
              key={`c-${container.id}`}
              container={container}
              subCount={subCount}
              exerciseCount={exs.length}
              previewNames={exs.map((e) => e.name)}
              onClick={() => openFolder(container)}
              onRename={() => openRename(container)}
              onDelete={() => {
                setContainerToDelete(container);
                setOpenModal("deleteExerciseContainer");
              }}
              onDropExercise={handleDropExercise}
            />
          );
        })}

        {exercisesToShow.map((exercise) => (
          <ExerciseCard
            key={`e-${exercise.id}`}
            exercise={exercise}
            color={searching ? containerColorOf(exercise) : currentContainer?.color}
            onMoveToRoot={
              (searching ? exercise.idContainer != null : currentContainerId != null)
                ? () => handleDropExercise(exercise.id, null)
                : undefined
            }
            onEdit={() => {
              setExerciseToEdit(exercise);
              setOpenModal("edit-exercise");
            }}
            onDelete={() => {
              setExerciseToDelete(exercise);
              setOpenModal("delete-exercise");
            }}
            selectionMode={selectionMode}
            selected={selectedIds.has(exercise.id)}
            onToggleSelect={() => toggleSelect(exercise.id)}
          />
        ))}

        {foldersToShow.length === 0 && exercisesToShow.length === 0 && (
          <div className="col-span-full text-center text-gray-400 py-8">
            {searching
              ? "Nessun risultato per la ricerca."
              : currentContainerId == null
              ? "Nessuna cartella. Creane una per organizzare gli esercizi."
              : "Cartella vuota. Trascina qui gli esercizi o creane di nuovi."}
          </div>
        )}
      </div>

      {/* Esercizi orfani: nota nella radice */}
      {!searching &&
        currentContainerId == null &&
        orphanExercises.length > 0 &&
        foldersToShow.length > 0 && (
          <p className="mt-6 text-xs text-gray-400">
            Gli esercizi qui sotto non sono in nessuna cartella: trascinali su una
            cartella per organizzarli.
          </p>
        )}

      {/* Modals */}
      <CreateExerciseContainerModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        parentId={createParentId}
      />
      <DeleteExerciseContainerModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        container={containerToDelete}
        exercisesCount={
          containerToDelete ? exercisesIn(containerToDelete.id).length : 0
        }
        subCount={
          containerToDelete ? childrenOf(containerToDelete.id).length : 0
        }
      />
      {openModal === "create-exercise" && (
        <CreateExerciseModal
          openModal={openModal}
          setOpenModal={(v) => setOpenModal(v)}
        />
      )}
      {openModal === "edit-exercise" && exerciseToEdit && (
        <EditExerciseModal
          key={exerciseToEdit.id}
          openModal={openModal}
          setOpenModal={(v) => {
            setOpenModal(v);
            if (!v) setExerciseToEdit(undefined);
          }}
          exerciseToUpdate={exerciseToEdit}
        />
      )}
      {openModal === "delete-exercise" && exerciseToDelete && (
        <DeleteExerciseModal
          openModal={openModal}
          setOpenModal={(v) => {
            setOpenModal(v);
            if (!v) setExerciseToDelete(undefined);
          }}
          exerciseToDelete={exerciseToDelete}
        />
      )}

      {/* Bulk delete confirmation modal */}
      {confirmBulkDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Elimina esercizi selezionati
            </h3>
            <p className="mb-4 text-gray-600 dark:text-gray-300">
              Vuoi eliminare definitivamente{" "}
              <strong>{selectedIds.size} eserciz{selectedIds.size !== 1 ? "i" : "io"}</strong>?
              L&apos;operazione non e' reversibile.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmBulkDelete(false)}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800"
              >
                Annulla
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {bulkDeleting ? "Eliminazione..." : "Elimina"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename modal */}
      {openModal === "renameExerciseContainer" && containerToRename && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Rinomina cartella
            </h3>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="block w-full py-2 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameSubmit();
              }}
            />
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Colore
              </label>
              <ColorPicker value={renameColor} onChange={setRenameColor} />
            </div>
            {renameError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                Errore durante il salvataggio. Riprova.
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setOpenModal(undefined);
                  setContainerToRename(undefined);
                  setRenameError(false);
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800"
              >
                Annulla
              </button>
              <button
                onClick={handleRenameSubmit}
                disabled={renameSaving}
                className="px-4 py-2 text-sm text-white bg-primary-700 hover:bg-primary-800 rounded-lg disabled:opacity-50"
              >
                {renameSaving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
